import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { SupportedFunctions, useEventFunctions } from '../../../../../hooks/useEventFunctions';
import { DetailedTicketType } from '../../../../../interfaces/interfaces';
import { SignupFormComponentInterface } from '../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { getAllDetailedTicketTypesForEvent } from '../../../../../services/services';
import { PageBuilderSettingsSection, PageBuilderSettingsTable } from '../PageBuilderSettingsTable';
import React from 'react';

interface SignupFormSettingsTableInterface {
  component: SignupFormComponentInterface;
  onChange: (c: SignupFormComponentInterface) => void;
}

const SignupFormSettingsTable = ({ component, onChange }: SignupFormSettingsTableInterface) => {
  const { t } = useTranslation(['pagebuilder']);

  const altSignups = useSelector((state) => state.admin.altSignups);
  const event = useSelector((state) => state.common.event);

  const { isFunctionEnabled } = useEventFunctions();

  const [ticketTypes, setTicketTypes] = useState<DetailedTicketType[]>();
  const [loading, setLoading] = useState(false);
  const [showTicketGate, setShowTicketGate] = useState<boolean>(false);

  const fetchTypes = async () => {
    if (!event) return;
    setLoading(true);
    try {
      const types = await getAllDetailedTicketTypesForEvent(event._id);
      setTicketTypes(types);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!ticketTypes) fetchTypes();
  }, [event]);

  useEffect(() => {
    if (!component.showTicketGate || !event) return;
    setShowTicketGate(component.showTicketGate && isFunctionEnabled(SupportedFunctions.tickets, event) && !!ticketTypes && ticketTypes?.length > 0);
    console.log('showTicketGate', showTicketGate);
  }, [component.showTicketGate, event, ticketTypes]);

  const settingSections: PageBuilderSettingsSection[] = [
    {
      name: t('display-settings'),
      settings: [
        {
          label: t('pagebuilder:show-form-header'),
          type: 'boolean',
          path: 'showFormHeader',
        },
        {
          label: t('pagebuilder:signup-button-text'),
          type: 'text',
          path: 'signupButtonText',
          placeholder: t('auth:accept-signin').toString(),
        },
        {
          label: t('pagebuilder:select-alt-signup'),
          type: 'select',
          path: 'altSignupId',
          defaultValue: component.altSignupId,
          showNullItem: true,
          nullItemTranslation: t('account:hybrid.standard-signup'),
          selectItems: altSignups.map((p) => ({ name: p.name!, value: p._id! })),
          visible: altSignups.length > 0,
        },
        {
          label: t('pagebuilder:show-ticketgate'),
          type: 'boolean',
          path: 'showTicketGate',
          isLoading: loading,
          visible: ticketTypes && ticketTypes?.length > 0 && isFunctionEnabled(SupportedFunctions.tickets, event),
          onChange: (value) => {
            onChange({
              ...component,
              showTicketGate: value,
              ...(component.redirectToOrderOverview && component.showTicketGate ? { redirectToOrderOverview: false } : {}),
            });
          },
        },
        {
          label: t('config:showDefaultDataProtectionNotice-title'),
          type: 'boolean',
          path: 'showDefaultDataProtectionNotice',
        },
        {
          label: t('config:dataPrivacySentence-title'),
          path: 'dataPrivacySentence',
          type: 'text',
          visible: !component.showDefaultDataProtectionNotice,
        },
      ],
    },
    {
      name: t('signup-header-settings'),
      visible: component.showFormHeader,
      settings: [
        {
          label: t('pagebuilder:show-form-header-title'),
          type: 'boolean',
          path: 'showFormHeaderTitle',
        },
        {
          label: t('pagebuilder:show-form-header-date'),
          type: 'boolean',
          path: 'showFormHeaderEventDate',
        },
        {
          label: t('pagebuilder:show-form-header-time'),
          type: 'boolean',
          path: 'showFormHeaderEventTime',
        },
        {
          label: t('pagebuilder:show-form-header-region'),
          type: 'boolean',
          path: 'showFormHeaderRegion',
        },
        {
          label: t('pagebuilder:show-form-header-host'),
          type: 'boolean',
          path: 'showFormHeaderHost',
        },
        {
          label: t('pagebuilder:show-form-header-logo'),
          type: 'boolean',
          path: 'showFormHeaderLogo',
        },
      ],
    },
    {
      name: t('signup-forwarding'),
      settings: [
        {
          label: t('pagebuilder:redirect-to-signup-success'),
          type: 'boolean',
          disabled: component.redirectToUrl || (component.redirectToOrderOverview && showTicketGate),
          path: 'redirectToSignupSuccess',
        },
        {
          label: t('pagebuilder:redirect-to-order-overview'),
          type: 'boolean',
          disabled: component.redirectToUrl || component.redirectToSignupSuccess || !showTicketGate,
          path: 'redirectToOrderOverview',
          visible: showTicketGate,
        },
        {
          label: t('pagebuilder:redirect-to-url-switch'),
          type: 'boolean',
          disabled: component.redirectToSignupSuccess || (component.redirectToOrderOverview && showTicketGate),
          path: 'redirectToUrl',
        },
        {
          visible: component.redirectToUrl,
          type: 'text',
          path: 'redirectUrl',
          label: t('pagebuilder:redirect-to-url'),
        },
        {
          visible: component.redirectToUrl && !showTicketGate,
          label: t('pagebuilder:newtab-success'),
          type: 'boolean',
          path: 'newTabOnSuccess',
        },
      ],
    },
    {
      name: t('signup-registration-settings'),
      settings: [
        {
          label: t('pagebuilder:multiple-tickets'),
          type: 'boolean',
          path: 'multipleTickets',
        },
        {
          label: t('pagebuilder:show-user-registered-info'),
          type: 'boolean',
          path: 'showUserRegisteredInfo',
        },
        {
          visible: component.showUserRegisteredInfo,
          type: 'boolean',
          path: 'showBackToEventButton',
          label: t('pagebuilder:show-back-to-event-for-registered'),
        },
        {
          label: t('pagebuilder:user-registered-info'),
          visible: component.showUserRegisteredInfo,
          type: 'text',
          path: 'userRegisteredInfo',
        },
        {
          type: 'boolean',
          path: 'forceNewMail',
          label: t('pagebuilder:force-new-signup-mail'),
        },
        {
          type: 'boolean',
          path: 'showEmailConfirmation',
          label: t('pagebuilder:show-email-confirmation'),
        },
        {
          type: 'boolean',
          path: 'hideGeneratedEmail',
          label: t('pagebuilder:hide-generated-email'),
          tooltip: t('pagebuilder:hide-generated-email-tooltip'),
        },
      ],
    },
  ];

  return <PageBuilderSettingsTable sections={settingSections} onChange={onChange} component={component} />;
};
export default SignupFormSettingsTable;
